/**
 * WebSocket client for real-time updates with automatic reconnection
 * and fallback to polling
 */

import { connectionApi, ConnectionSessionResponse } from '../api/connection';

export interface WebSocketMessage {
  type: string;
  [key: string]: unknown;
}

// Specific message type definitions for type safety
export interface FriendRequestReceivedMessage extends WebSocketMessage {
  type: 'friend_request_received';
  sender_name: string;
  sender_id: string;
}

export interface FriendRequestAcceptedMessage extends WebSocketMessage {
  type: 'friend_request_accepted';
  friend_name: string;
  friend_id: string;
}

export interface SessionCreatedMessage extends WebSocketMessage {
  type: 'session_created';
  requester_name: string;
  session_id: string;
}

export interface SessionUpdatedMessage extends WebSocketMessage {
  type: 'session_updated';
  status: 'approved' | 'rejected' | 'active' | 'ended';
  session_id: string;
}

export interface SessionEndedMessage extends WebSocketMessage {
  type: 'session_ended';
  session_id: string;
  reason: string;
}

export interface VPNStatusChangedMessage extends WebSocketMessage {
  type: 'vpn_status_changed';
  sharing_enabled: boolean;
  user_id: string;
}

export interface BandwidthUpdateMessage extends WebSocketMessage {
  type: 'bandwidth_update';
  session_id: string;
  timestamp: string;
  bytes_sent: number;
  bytes_received: number;
  bytes_per_second_sent: number;
  bytes_per_second_received: number;
}

export type MessageHandler = (message: WebSocketMessage) => void;

interface WebSocketClientOptions {
  /** Authentication token */
  token: string;
  /** Callback when connection opens */
  onOpen?: () => void;
  /** Callback when connection closes */
  onClose?: () => void;
  /** Callback when connection errors */
  onError?: (error: Event) => void;
  /** Callback for incoming messages */
  onMessage?: MessageHandler;
  /** Enable automatic reconnection (default: true) */
  autoReconnect?: boolean;
  /** Reconnection delay in ms (default: 5000) */
  reconnectDelay?: number;
  /** Maximum reconnection attempts (default: Infinity) */
  maxReconnectAttempts?: number;
}

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private token: string;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private reconnectAttempts = 0;
  private messageHandlers: Set<MessageHandler> = new Set();
  private isIntentionallyClosed = false;

  // Options with defaults
  private onOpen?: () => void;
  private onClose?: () => void;
  private onError?: (error: Event) => void;
  private onMessage?: MessageHandler;
  private autoReconnect: boolean;
  private reconnectDelay: number;
  private maxReconnectAttempts: number;

  constructor(options: WebSocketClientOptions) {
    this.token = options.token;
    this.onOpen = options.onOpen;
    this.onClose = options.onClose;
    this.onError = options.onError;
    this.onMessage = options.onMessage;
    this.autoReconnect = options.autoReconnect ?? true;
    this.reconnectDelay = options.reconnectDelay ?? 5000;
    this.maxReconnectAttempts = options.maxReconnectAttempts ?? Infinity;
  }

  /**
   * Connect to WebSocket server
   *
   * New flow with distributed connection nodes:
   * 1. Request session from API (gets assigned node + JWT token)
   * 2. Connect to assigned node using session token
   */
  async connect(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    this.isIntentionallyClosed = false;

    try {
      // Step 1: Request connection session from API
      const sessionResponse = await connectionApi.requestSession();
      const session: ConnectionSessionResponse = sessionResponse.data;

      // Step 2: Connect to assigned connection node
      const wsProtocol = session.host.includes('localhost') ? 'ws:' : 'wss:';
      const wsUrl = `${wsProtocol}//${session.host}/ws`;

      // Use session token in Sec-WebSocket-Protocol header
      this.ws = new WebSocket(wsUrl, [`bearer.${session.token}`]);

      this.ws.onopen = () => {
        this.reconnectAttempts = 0;
        if (this.onOpen) {
          this.onOpen();
        }
      };

      this.ws.onclose = (event) => {
        if (this.onClose) {
          this.onClose();
        }

        // Attempt reconnection if not intentionally closed
        if (!this.isIntentionallyClosed && this.autoReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (error) => {
        console.error('[WebSocket] Error:', error);
        if (this.onError) {
          this.onError(error);
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as WebSocketMessage;

          // Call global onMessage handler
          if (this.onMessage) {
            this.onMessage(message);
          }

          // Call all registered message handlers
          this.messageHandlers.forEach(handler => {
            try {
              handler(message);
            } catch (error) {
              console.error('[WebSocket] Handler error:', error);
            }
          });
        } catch (error) {
          console.error('[WebSocket] Failed to parse message:', error);
        }
      };

    } catch (error) {
      console.error('[WebSocket] Connection failed:', error);
      if (this.autoReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.scheduleReconnect();
      }
    }
  }

  /**
   * Schedule reconnection attempt
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * this.reconnectAttempts, 30000); // Max 30s

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.isIntentionallyClosed = true;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * Add a message handler
   */
  addMessageHandler(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);

    // Return cleanup function
    return () => {
      this.messageHandlers.delete(handler);
    };
  }

  /**
   * Send a message (currently WebSocket is receive-only for our use case)
   */
  send(data: unknown): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn('[WebSocket] Cannot send - not connected');
    }
  }

  /**
   * Get current connection state
   */
  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Get current ready state
   */
  get readyState(): number {
    return this.ws?.readyState ?? WebSocket.CLOSED;
  }
}
